import { cloneElement, isValidElement, useState } from 'react';
import type { KeyboardEvent, ReactElement, ReactNode } from 'react';
import { useTranslate } from '@refinedev/core';
import { Button, Flex, Form, Input, Typography, theme } from 'antd';
import {
  DeleteOutlined,
  HolderOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface OrderedRelationRowsProps {
  /** Form.List field name, e.g. `slots`. */
  name: string;
  /** Row field holding the related id, e.g. `publication_id`. */
  valueField: string;
  /** i18n prefix providing .placeholder .add .remove .required .duplicate */
  i18nPrefix: string;
  /** Per-row picker element; receives antd's injected value/onChange plus
   * autoFocus/defaultOpen/onBlur while the row is in pick mode. */
  picker: ReactElement;
  /** Display renderer for a picked row. Without it rows stay in pick mode —
   * a plain ordered select list. */
  renderRow?: (value: number) => ReactNode;
  /** Marks a row as problematic (red bar + tinted background). */
  rowError?: (value: number) => boolean;
  /** Card chrome around each row; off for plain select lists. */
  bordered?: boolean;
  /** Control docked to the right of a picked row, e.g. a section's display
   * mode. Gets the Form.List row index so it can address its own nested
   * `Form.Item`. Not called while the row is picking: the picker owns the
   * full width there, and a fresh row opens its dropdown over it. */
  renderRowExtra?: (row: { index: number; value: number }) => ReactNode;
  /** Seed values for a row created from the add button. */
  newRowDefaults?: Record<string, unknown>;
}

interface OrderedRelationRowProps {
  id: number;
  ordinal: number;
  error: boolean;
  bordered: boolean;
  onRemove: () => void;
  removeLabel: string;
  extra?: ReactNode;
  children: ReactNode;
}

// Row identity is the stable Form.List field key, so drag and edit state
// survive renumbering.
const OrderedRelationRow = ({
  id,
  ordinal,
  error,
  bordered,
  onRemove,
  removeLabel,
  extra,
  children,
}: OrderedRelationRowProps) => {
  const { token } = theme.useToken();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
        zIndex: isDragging ? 1 : undefined,
        position: 'relative',
        ...(bordered
          ? {
              border: `1px solid ${token.colorBorderSecondary}`,
              borderLeft: `3px solid ${error ? token.colorError : token.colorBorderSecondary}`,
              borderRadius: token.borderRadiusLG,
              // Softened so the tint stays readable over both admin themes.
              background: error
                ? `color-mix(in srgb, ${token.colorErrorBg} 55%, ${token.colorBgContainer})`
                : token.colorBgContainer,
              padding: token.paddingXS,
            }
          : undefined),
      }}
    >
      <Flex gap={token.marginXS} align={bordered ? 'center' : 'baseline'}>
        {/* Handle and ordinal read as one grip — only the content column is
            a gap away. */}
        <Flex align="center" gap={0}>
          <Button
            ref={setActivatorNodeRef}
            type="text"
            size="small"
            icon={<HolderOutlined />}
            style={{
              cursor: 'grab',
              touchAction: 'none',
              paddingInline: 0,
              width: token.controlHeightSM,
            }}
            {...attributes}
            {...listeners}
          />
          <Typography.Text
            type="secondary"
            style={{ width: 20, textAlign: 'right', flexShrink: 0 }}
          >
            {ordinal}.
          </Typography.Text>
        </Flex>
        <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
        {/* Outside the content column, so it is never inside the row's
            double-click-to-re-pick region. */}
        {extra != null && <div style={{ flexShrink: 0 }}>{extra}</div>}
        <Button
          type="text"
          icon={<DeleteOutlined />}
          onClick={onRemove}
          aria-label={removeLabel}
        />
      </Flex>
    </div>
  );
};

/**
 * Ordered rows over a Form.List: drag to reorder, add/remove, duplicate
 * validation, and — when a display renderer is given — double-click a row to
 * re-pick it. Row order is the only order; callers derive positions on
 * submit. Agnostic about what is ordered and how it looks: the consumer
 * supplies the picker and the row renderer.
 */
export const OrderedRelationRows = ({
  name,
  valueField,
  i18nPrefix,
  picker,
  renderRow,
  rowError,
  bordered = true,
  renderRowExtra,
  newRowDefaults,
}: OrderedRelationRowsProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const form = Form.useFormInstance();
  // Rows carry more than ids now (a section's display mode), so the watch
  // cannot promise numbers.
  const rows =
    Form.useWatch<Array<Record<string, unknown>> | undefined>(name, form) ?? [];
  // Rows the user explicitly re-opened; empty rows are always in pick mode.
  const [editingKeys, setEditingKeys] = useState<number[]>([]);

  // Distance keeps plain clicks on the handle from starting a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  const startEditing = (key: number) =>
    setEditingKeys((keys) => [...keys, key]);

  const stopEditing = (key: number) =>
    setEditingKeys((keys) => keys.filter((k) => k !== key));

  // Later copies are the duplicates; the first occurrence stays clean.
  const isDuplicate = (value: number | undefined, index: number) =>
    value != null &&
    rows.findIndex((row) => row?.[valueField] === value) !== index;

  return (
    <Form.List name={name}>
      {(fields, { add, remove, move }) => {
        const onDragEnd = ({ active, over }: DragEndEvent) => {
          if (over == null || active.id === over.id) return;
          const from = fields.findIndex((f) => f.key === active.id);
          const to = fields.findIndex((f) => f.key === over.id);
          if (from >= 0 && to >= 0) move(from, to);
        };

        return (
          <Flex vertical gap={token.marginXS}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
            >
              <SortableContext
                items={fields.map((field) => field.key)}
                strategy={verticalListSortingStrategy}
              >
                {fields.map((field, index) => {
                  const value = rows[field.name]?.[valueField] as
                    | number
                    | undefined;
                  // No display renderer means there is nothing to flip to.
                  const picking =
                    renderRow == null ||
                    value == null ||
                    editingKeys.includes(field.key);

                  return (
                    <OrderedRelationRow
                      key={field.key}
                      id={field.key}
                      ordinal={index + 1}
                      error={
                        isDuplicate(value, field.name) ||
                        (value != null && (rowError?.(value) ?? false))
                      }
                      bordered={bordered}
                      onRemove={() => {
                        stopEditing(field.key);
                        remove(field.name);
                      }}
                      removeLabel={t(`${i18nPrefix}.remove`)}
                      extra={
                        !picking && value != null
                          ? renderRowExtra?.({ index: field.name, value })
                          : undefined
                      }
                    >
                      <Form.Item name={[field.name, 'id']} hidden>
                        <Input />
                      </Form.Item>

                      {/* Never both: the id lives in the picker while picking,
                          in a hidden item while displaying. */}
                      <Form.Item
                        name={[field.name, valueField]}
                        hidden={!picking}
                        style={{ marginBottom: 0 }}
                        rules={[
                          {
                            required: true,
                            message: t(`${i18nPrefix}.required`),
                          },
                          ({ getFieldValue }) => ({
                            // Only later copies fail, so the message and the
                            // red bar accuse the same row.
                            validator: (_, current: number | undefined) => {
                              const all: Array<
                                Record<string, unknown> | undefined
                              > = getFieldValue(name) ?? [];
                              const first = all.findIndex(
                                (row) => row?.[valueField] === current
                              );
                              return current != null &&
                                first !== -1 &&
                                first !== field.name
                                ? Promise.reject(
                                    new Error(t(`${i18nPrefix}.duplicate`))
                                  )
                                : Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        {picking && isValidElement(picker) ? (
                          cloneElement(
                            picker as ReactElement<Record<string, unknown>>,
                            {
                              // The editor's own copy wins over whatever the
                              // picker says when it stands alone.
                              placeholder: t(`${i18nPrefix}.placeholder`),
                              autoFocus: renderRow != null,
                              defaultOpen: renderRow != null && value == null,
                              style: { width: '100%' },
                              // Abandoning a re-pick returns to display;
                              // an empty row has nothing to return to.
                              onBlur: () => stopEditing(field.key),
                              onKeyDown: (event: KeyboardEvent) => {
                                if (event.key === 'Escape')
                                  stopEditing(field.key);
                              },
                            }
                          )
                        ) : (
                          // Keeps the id registered without mounting a second
                          // picker (and its query) per displayed row.
                          <Input />
                        )}
                      </Form.Item>

                      {!picking && value != null && renderRow != null && (
                        <div
                          role="button"
                          tabIndex={0}
                          aria-label={t(`${i18nPrefix}.edit`)}
                          onDoubleClick={() => startEditing(field.key)}
                          // Double-click has no keyboard equivalent; without
                          // this the row is unreachable without a pointer.
                          onKeyDown={(event) => {
                            if (event.key !== 'Enter' && event.key !== ' ')
                              return;
                            event.preventDefault();
                            startEditing(field.key);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          {renderRow(value)}
                        </div>
                      )}

                      {/* The Form.Item carrying this rule is hidden in display
                          mode, so its own message would never be seen. */}
                      {!picking && isDuplicate(value, field.name) && (
                        <Typography.Text type="danger">
                          {t(`${i18nPrefix}.duplicate`)}
                        </Typography.Text>
                      )}
                    </OrderedRelationRow>
                  );
                })}
              </SortableContext>
            </DndContext>

            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => add({ ...newRowDefaults })}
              block
            >
              {t(`${i18nPrefix}.add`)}
            </Button>
          </Flex>
        );
      }}
    </Form.List>
  );
};
